import { useEffect, useState } from "react";

const INSTRUMENT_PARAMS = ["instrument", "ccyPair"];
const FDC3_CONTEXT_TYPES = [
  "fdc3.instrument",
  "fdc3.instrumentList",
  "bestx.selectedTrade",
];
export const EMBED_MESSAGE_TYPE = "emalpha.context";

// ISO 4217 codes (plus offshore CNH and precious metals) used to tell an FX
// pair from an equity or other instrument sharing the channel.
const CURRENCY_CODES = new Set(
  (
    "AED AFN ALL AMD ANG AOA ARS AUD AWG AZN BAM BBD BDT BGN BHD BIF BMD BND " +
    "BOB BRL BSD BTN BWP BYN BZD CAD CDF CHF CLP CNH CNY COP CRC CUP CVE CZK " +
    "DJF DKK DOP DZD EGP ERN ETB EUR FJD FKP GBP GEL GHS GIP GMD GNF GTQ GYD " +
    "HKD HNL HTG HUF IDR ILS INR IQD IRR ISK JMD JOD JPY KES KGS KHR KMF KPW " +
    "KRW KWD KYD KZT LAK LBP LKR LRD LSL LYD MAD MDL MGA MKD MMK MNT MOP MRU " +
    "MUR MVR MWK MXN MYR MZN NAD NGN NIO NOK NPR NZD OMR PAB PEN PGK PHP PKR " +
    "PLN PYG QAR RON RSD RUB RWF SAR SBD SCR SDG SEK SGD SHP SLE SOS SRD SSP " +
    "STN SYP SZL THB TJS TMT TND TOP TRY TTD TWD TZS UAH UGX USD UYU UZS VES " +
    "VND VUV WST XAF XCD XOF XPF YER ZAR ZMW ZWL XAU XAG XPT XPD"
  ).split(" "),
);

// "eur/usd" -> "EURUSD", "brk.b" -> "BRK.B". Any asset class passes through;
// each screen decides what it can show (CoTrader matches its whole universe).
const normalizeTicker = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9.]/g, "");

// The FX pair in a ticker ("EUR/USD" -> "EURUSD"), or null for anything that
// isn't two distinct currency codes, e.g. an equity like "AAPL" or "VOD.L".
export const toFxPair = (value) => {
  const ticker = String(value || "")
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase();
  if (ticker.length !== 6) return null;
  const base = ticker.slice(0, 3);
  const quote = ticker.slice(3);
  if (base === quote || !CURRENCY_CODES.has(base) || !CURRENCY_CODES.has(quote)) {
    return null;
  }
  return ticker;
};

const instrumentListFor = (...tickers) => ({
  type: "fdc3.instrumentList",
  instruments: tickers.map((ticker) => ({ type: "fdc3.instrument", id: { ticker } })),
});

// Every shape becomes an fdc3.instrumentList. CoTrader and CoHedger read
// `instruments[0].id.ticker`; the FX tiles take the first FX pair in the list.
export const normalizeContext = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  if (raw.type === "fdc3.instrument") {
    const ticker = normalizeTicker(raw.id?.ticker ?? raw.name);
    return ticker ? instrumentListFor(ticker) : null;
  }
  if (raw.type === "fdc3.instrumentList") {
    const tickers = (raw.instruments || [])
      .map((instrument) => normalizeTicker(instrument?.id?.ticker))
      .filter(Boolean);
    return tickers.length ? instrumentListFor(...tickers) : null;
  }
  if (raw.type === "bestx.selectedTrade") return raw;
  return null;
};

export const contextFromSearch = (search) => {
  const params = new URLSearchParams(search);
  for (const name of INSTRUMENT_PARAMS) {
    const ticker = normalizeTicker(params.get(name));
    if (ticker) return instrumentListFor(ticker);
  }
  return null;
};

// Context for a standalone screen: URL params at launch, then live updates from
// a standard FDC3 desktop agent (window.fdc3) or a postMessage from the host.
const useEmbedContext = () => {
  const [context, setContext] = useState(() =>
    contextFromSearch(window.location.search),
  );

  useEffect(() => {
    let disposed = false;
    const listeners = [];

    const publish = (raw) => {
      const next = normalizeContext(raw);
      if (next && !disposed) setContext(next);
    };

    const attachFdc3 = async () => {
      if (!window.fdc3 || disposed) return;
      for (const type of FDC3_CONTEXT_TYPES) {
        try {
          const listener = await window.fdc3.addContextListener(type, publish);
          if (disposed) listener?.unsubscribe?.();
          else listeners.push(listener);
        } catch (error) {
          console.warn(`[EMALPHA][embed] fdc3 listener ${type} failed`, error);
        }
      }
    };

    const onMessage = (event) => {
      if (event.source !== window.opener && event.source !== window.parent) {
        return;
      }
      if (event.data?.type === EMBED_MESSAGE_TYPE) publish(event.data.context);
    };

    if (window.fdc3) attachFdc3();
    else window.addEventListener("fdc3Ready", attachFdc3, { once: true });
    window.addEventListener("message", onMessage);

    return () => {
      disposed = true;
      window.removeEventListener("fdc3Ready", attachFdc3);
      window.removeEventListener("message", onMessage);
      listeners.forEach((l) => l?.unsubscribe?.());
    };
  }, []);

  return context;
};

export default useEmbedContext;
